import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CourseMaterial,
  CreateCourseMaterialDto,
  RegisterCourseMaterialDto,
  UpdateCourseMaterialDto,
} from '../interfaces/course-material';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
  ApiSimpleResponse,
} from '../interfaces/responses/api-response';

/**
 * Professor endpoints for managing own course materials.
 *
 * POST   /api/professor/course-materials             (multipart upload)
 * GET    /api/professor/course-materials             (own list)
 * GET    /api/professor/course-materials/{id}
 * PUT    /api/professor/course-materials/{id}        (update metadata)
 * DELETE /api/professor/course-materials/{id}        (archive)
 * GET    /api/professor/course-materials/{id}/download
 */
@Injectable({ providedIn: 'root' })
export class ProfessorCourseMaterialService {
  private readonly base = `${environment.BASE_URL}/professor/course-materials`;

  constructor(private http: HttpClient) {}

  getMyMaterials(includeArchived = false): Observable<ApiCollectionResponse<CourseMaterial>> {
    const params = new HttpParams().set('includeArchived', String(includeArchived));
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(this.base, { params });
  }

  /**
   * Mes supports deposes mais non encore diffuses.
   *
   * <p>L'enregistrement d'une seance de production nait prive : le professeur
   * produit d'abord, decide ensuite. Sans cette liste, la video resterait
   * invisible faute d'endroit ou la retrouver.</p>
   */
  getUnpublished(): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(`${this.base}/unpublished`);
  }

  /** Diffuse un support, ou le retire de la diffusion. */
  setPublished(id: number, published: boolean): Observable<ApiSimpleResponse> {
    return this.http.patch<ApiSimpleResponse>(`${this.base}/${id}/published`, { published });
  }

  /** Remet un support archive en circulation. Ne retablit pas la diffusion. */
  unarchive(id: number): Observable<ApiSimpleResponse> {
    return this.http.patch<ApiSimpleResponse>(`${this.base}/${id}/unarchive`, {});
  }

  getById(id: number): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.get<ApiSingleResponse<CourseMaterial>>(`${this.base}/${id}`);
  }

  /**
   * Upload a new material. Build the FormData before calling:
   *   const fd = new FormData();
   *   Object.entries(dto).forEach(([k,v]) => v != null && fd.append(k, String(v)));
   *   if (file) fd.append('file', file);
   */
  upload(formData: FormData): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.post<ApiSingleResponse<CourseMaterial>>(this.base, formData);
  }

  /**
   * Enregistre un support déjà déposé sur le stockage (dépôt présigné).
   *
   * À appeler après le PUT direct : le fichier est déjà en place, il ne reste
   * qu'à le rattacher à la bibliothèque — et, le cas échéant, à une séance.
   */
  register(
    dto: RegisterCourseMaterialDto,
  ): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.post<ApiSingleResponse<CourseMaterial>>(
      `${this.base}/registered`,
      dto,
    );
  }

  updateMeta(
    id: number,
    dto: UpdateCourseMaterialDto,
  ): Observable<ApiSingleResponse<CourseMaterial>> {
    return this.http.put<ApiSingleResponse<CourseMaterial>>(`${this.base}/${id}`, dto);
  }

  /** Logical delete / archive. */
  archive(id: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.base}/${id}`);
  }

  download(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/${id}/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }
}
